/**
 * Copyright © 2025-2026 Delicious AI, LLC
 */

import styled from '@emotion/styled'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Drawer as MuiDrawer, IconButton, ListItemButton } from '@mui/material'
import React from 'react'
import { NavLink } from 'react-router-dom'

import useT from '@/i18n/useT'
import { SidebarItemsType } from '@/types/sidebar'

import DaiLogo from '../DaiLogo'
import SidebarNav from './SidebarNav'

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
  }
`

const Brand = styled(ListItemButton)<{
  component?: React.ElementType
  to?: string
}>`
  font-size: ${props => props.theme.typography.h5.fontSize};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  color: ${props => props.theme.sidebar.header.color};
  background-color: ${props => props.theme.sidebar.header.background};
  font-family: ${props => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${props => props.theme.spacing(6)};
  padding-right: ${props => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.12);

  ${props => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${props => props.theme.sidebar.header.background};
  }
`

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number
    }
  }
  variant?: 'permanent' | 'persistent' | 'temporary'
  open?: boolean
  onClose?: () => void
  items: {
    title: string
    pages: SidebarItemsType[]
  }[]
  showFooter?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = false,
  variant = 'permanent',
  collapsed = false,
  onToggleCollapse,
  ...rest
}) => {
  const { t } = useT()

  return (
    <Drawer variant={variant} {...rest}>
      <Brand
        component={NavLink}
        to="/stats"
        aria-label={t('sidebar.dashboard.ariaLabel', 'Dashboard')}
      >
        <DaiLogo
          compact={collapsed}
          sx={{
            width: collapsed ? 38 : 150,
            marginBottom: 0,
          }}
        />
        {variant === 'permanent' && onToggleCollapse && (
          <IconButton
            size="small"
            aria-label={t(
              'sidebar.toggleCollapse.ariaLabel',
              'Toggle sidebar collapse',
            )}
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onToggleCollapse()
            }}
            sx={{
              position: 'absolute',
              right: collapsed ? 6 : 10,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {collapsed ? (
              <ChevronRight fontSize="small" />
            ) : (
              <ChevronLeft fontSize="small" />
            )}
          </IconButton>
        )}
      </Brand>
      <SidebarNav items={items} collapsed={collapsed} />
      {/* TODO: Implement footer content when showFooter is true */}
    </Drawer>
  )
}

export default Sidebar
